import { useCallback, useEffect, useMemo } from 'react';
import { useFlashTransition } from 'hooks/useFlashState';
import { getOriginalError } from 'services/AxiosService';

const errorsOrder = {
  name: 1,
  action: 2,
  automation: 3,
  description: 4,
  sharing_settings: 5,
};

export const getErrors = (error) => {
  const { name, order, description, action, automation, sharing_settings } =
    getOriginalError(error) || {};

  return {
    name,
    order,
    description,
    action,
    automation,
    sharing_settings,
  };
};

export const useErrors = ({ id, errors, setErrors, enabled }) => {
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const [hasError, errorKey] = useMemo(() => {
    const hasError = !!errors && Object.values(errors).some(Boolean);
    return [
      hasError,
      hasError
        ? Object.entries(errors)
            .filter(([, error]) => error)
            .sort(([a], [b]) => errorsOrder[a] - errorsOrder[b])[0]?.[0]
        : null,
    ];
  }, [errors]);

  useEffect(() => {
    if (errorKey && errors[errorKey] && enabled) {
      flashErrorMessage(errors[errorKey]);
    }
  }, [errors, errorKey, flashErrorMessage, enabled]);

  const clearError = useCallback(
    (prop) => {
      if (hasError) {
        if (showMessage && errorKey === prop) {
          cancelFlash();
        }

        setErrors((prev) => ({
          ...prev,
          [id]: prev[id] && { ...prev[id], [prop]: undefined },
        }));
      }
    },
    [showMessage, cancelFlash, hasError, setErrors, errorKey, id]
  );

  const validate = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  return {
    hasError,
    errorKey,
    validate,
    clearError,
  };
};
